import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Link as LinkIcon, Copy, CircleCheck as CheckCircle, Clock, Circle as XCircle, Eye, RefreshCw, ClipboardCheck } from 'lucide-react';

interface RegistrationLink {
  id: string;
  slug: string;
  loan_amount: number;
  installments: number;
  interest_rate: number;
  total_amount: number;
  installment_amount: number;
  status: string;
  created_at: string;
  opened_at: string | null;
  completed_at: string | null;
  observations: string;
  contract_id: string | null;
  contract_approval_status: string | null;
}

interface SelfReg {
  contract_id: string | null;
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending: { label: 'Aguardando abertura', color: 'bg-gray-100 text-gray-600' },
  opened: { label: 'Aberto', color: 'bg-yellow-100 text-yellow-700' },
  completed: { label: 'Concluído', color: 'bg-emerald-100 text-emerald-700' },
  expired: { label: 'Expirado', color: 'bg-red-100 text-red-600' },
  cancelled: { label: 'Cancelado', color: 'bg-gray-200 text-gray-500' },
};

const CONTRACT_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  vendor_review: { label: 'Em revisao pelo vendedor', color: 'bg-orange-100 text-orange-700' },
  vendor_rejected: { label: 'Recusado pelo vendedor', color: 'bg-red-100 text-red-600' },
  pending: { label: 'Aguardando gestor', color: 'bg-blue-100 text-blue-700' },
  approved: { label: 'Aprovado', color: 'bg-emerald-100 text-emerald-700' },
  rejected: { label: 'Recusado pelo gestor', color: 'bg-red-100 text-red-600' },
};

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function formatDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

export default function RegistrationLinks() {
  const { user } = useAuth();
  const [links, setLinks] = useState<RegistrationLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);

  const fetchLinks = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const query = supabase
        .from('seller_registration_links')
        .select('*')
        .order('created_at', { ascending: false });

      if (user.role === 'seller') {
        query.eq('seller_id', user.id);
      } else {
        query.eq('unit_id', user.unit_id);
      }

      const { data, error } = await query;
      if (error) throw error;

      // For completed links, fetch contract_id and approval_status from self_registrations + contracts
      const enriched: RegistrationLink[] = await Promise.all(
        (data || []).map(async (link) => {
          if (link.status === 'completed') {
            const { data: reg } = await supabase
              .from('client_self_registrations')
              .select('contract_id')
              .eq('link_id', link.id)
              .maybeSingle();
            const contractId = (reg as SelfReg | null)?.contract_id || null;
            let contractApprovalStatus: string | null = null;
            if (contractId) {
              const { data: contract } = await supabase
                .from('contracts')
                .select('approval_status')
                .eq('id', contractId)
                .maybeSingle();
              contractApprovalStatus = contract?.approval_status || null;
            }
            return { ...link, contract_id: contractId, contract_approval_status: contractApprovalStatus };
          }
          return { ...link, contract_id: null, contract_approval_status: null };
        })
      );

      setLinks(enriched);
    } catch (err) {
      console.error('Error fetching links:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLinks();
  }, [user]);

  const copyLink = (slug: string) => {
    const domain = import.meta.env.VITE_LEADS_DOMAIN || window.location.origin;
    const url = `${domain}/cadastro/${slug}`;
    navigator.clipboard.writeText(url);
    setCopiedSlug(slug);
    setTimeout(() => setCopiedSlug(null), 2000);
  };

  const cancelLink = async (id: string) => {
    if (!window.confirm('Deseja cancelar este link? O cliente não poderá mais acessá-lo.')) return;
    setCancelling(id);
    try {
      await supabase
        .from('seller_registration_links')
        .update({ status: 'cancelled', is_active: false })
        .eq('id', id);
      await fetchLinks();
    } catch (err) {
      console.error('Error cancelling link:', err);
    } finally {
      setCancelling(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 lg:p-0">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-800">Links de Cadastro</h1>
            <p className="text-sm text-gray-500 mt-1">Acompanhe os links enviados para clientes.</p>
          </div>
          <button
            onClick={fetchLinks}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : links.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <LinkIcon className="w-7 h-7 text-gray-400" />
            </div>
            <p className="text-gray-500 font-medium">Nenhum link gerado ainda</p>
            <p className="text-sm text-gray-400 mt-1">
              Acesse Simulação de Empréstimo e escolha "Cadastro pelo Cliente" para gerar um link.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {links.map((link) => {
              const status = STATUS_LABELS[link.status] || STATUS_LABELS['pending'];
              const isCancellable = link.status === 'pending' || link.status === 'opened';
              const url = `${import.meta.env.VITE_LEADS_DOMAIN || window.location.origin}/cadastro/${link.slug}`;

              return (
                <div key={link.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 lg:p-5">
                  <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                    {/* Main info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${status.color}`}>
                          {link.status === 'completed' && <CheckCircle className="w-3 h-3" />}
                          {link.status === 'opened' && <Clock className="w-3 h-3" />}
                          {link.status === 'cancelled' && <XCircle className="w-3 h-3" />}
                          {status.label}
                        </span>
                        {link.status === 'completed' && link.contract_approval_status && CONTRACT_STATUS_LABELS[link.contract_approval_status] && (
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${CONTRACT_STATUS_LABELS[link.contract_approval_status].color}`}>
                            {link.contract_approval_status === 'vendor_review' && <ClipboardCheck className="w-3 h-3" />}
                            {CONTRACT_STATUS_LABELS[link.contract_approval_status].label}
                          </span>
                        )}
                        <span className="text-xs text-gray-400">{formatDate(link.created_at)}</span>
                      </div>

                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                        <div>
                          <p className="text-xs text-gray-400 mb-0.5">Empréstimo</p>
                          <p className="text-sm font-semibold text-gray-900">{formatCurrency(link.loan_amount)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 mb-0.5">Total a pagar</p>
                          <p className="text-sm font-semibold text-gray-900">{formatCurrency(link.total_amount)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 mb-0.5">Parcelas</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {link.installments}x {formatCurrency(link.installment_amount)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 mb-0.5">Juros</p>
                          <p className="text-sm font-semibold text-gray-900">{link.interest_rate}%</p>
                        </div>
                      </div>

                      {link.status !== 'pending' && (
                        <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                          {link.opened_at && (
                            <span>Aberto: {formatDate(link.opened_at)}</span>
                          )}
                          {link.completed_at && (
                            <span className="text-emerald-600 font-medium">Concluído: {formatDate(link.completed_at)}</span>
                          )}
                        </div>
                      )}

                      {link.status !== 'completed' && link.status !== 'cancelled' && link.status !== 'expired' && (
                        <div className="mt-3 flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                          <p className="text-xs text-gray-500 truncate flex-1 font-mono">{url}</p>
                          <button
                            onClick={() => copyLink(link.slug)}
                            className="flex-shrink-0 text-blue-500 hover:text-blue-700"
                            title="Copiar link"
                          >
                            {copiedSlug === link.slug
                              ? <CheckCircle className="w-4 h-4 text-emerald-500" />
                              : <Copy className="w-4 h-4" />}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex lg:flex-col gap-2 lg:gap-2 lg:min-w-[130px]">
                      {(link.status === 'pending' || link.status === 'opened') && (
                        <button
                          onClick={() => copyLink(link.slug)}
                          className="flex-1 lg:flex-none flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                        >
                          {copiedSlug === link.slug ? <CheckCircle className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                          {copiedSlug === link.slug ? 'Copiado!' : 'Copiar Link'}
                        </button>
                      )}

                      {link.status === 'completed' && link.contract_id && (
                        <a
                          href={`/dashboard/my-contracts`}
                          className="flex-1 lg:flex-none flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          Ver Contrato
                        </a>
                      )}

                      {isCancellable && (
                        <button
                          onClick={() => cancelLink(link.id)}
                          disabled={cancelling === link.id}
                          className="flex-1 lg:flex-none flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                          {cancelling === link.id ? 'Cancelando...' : 'Cancelar'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
